<script lang="ts" setup>
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { Popup, TextEllipsis } from "vant";
import EditIcon from "@/components/icon/editIcon.vue";
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import SkullIcon from "@/components/icon/SkullIcon.vue";
import { nextTick, ref, watch } from "vue";
import editorUtils from "@/modules/editor/editor-utils";
import { Editor } from "@tiptap/vue-3";
import { useStory } from "@/stores/story";

export type AiWritingMode = "prev" | "next" | "context" | "moment";
type WriteByContextParam = {
  mode: Omit<AiWritingMode, "moment">;
};
export type WriteByMomentParam = {
  momentId: number;
  momentText: string;
};

const props = withDefaults(
  defineProps<{
    editor: Editor;
    show?: boolean;
    storyId: number;
    mode?: Exclude<AiWritingMode, "moment">;
  }>(),
  {
    mode: "context",
    show: false,
  }
);
const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();

const storyStore = useStory();
const isAiLoading = ref(false);
const candidateTextList = ref<
  {
    loading: boolean;
    text: string;
  }[]
>([]);
const selectedCandidateIdx = ref(0);
const insertPosition = ref(0);
// Lifecycle
watch(
  () => props.show,
  (show) => {
    if (show) {
      props.editor.setEditable(false);
    }
    if (!show) {
      selectedCandidateIdx.value = 0;
      props.editor.setEditable(true);
    }
  }
);

// Event handlers
function writeByContext({ mode }: WriteByContextParam) {
  const prevCtx =
    mode === "prev" || mode === "context"
      ? editorUtils.getPrevContext(props.editor)
      : "";
  const nextCtx =
    mode === "next" || mode === "context"
      ? editorUtils.getNextContext(props.editor)
      : "";
  return storyStore.storyWriteByText(props.storyId, prevCtx, nextCtx);
}

function writeByMoment({ momentId, momentText }: WriteByMomentParam) {
  return storyStore.storyWriteByMoment(props.storyId, momentId, momentText);
}

const startAiWriting = async (param?: WriteByMomentParam) => {
  insertPosition.value = editorUtils.getFrom(props.editor);
  isAiLoading.value = true;
  try {
    editorUtils.removeAiTextNode(props.editor);
    candidateTextList.value = [];
    const promises: Promise<string>[] = [];
    for (let i = 0; i < 3; i++) {
      candidateTextList.value[i] = { loading: true, text: "" };
      let promise: Promise<string> | null = null;
      if (param) {
        promise = writeByMoment(param as WriteByMomentParam);
      } else {
        promise = writeByContext({ mode: props.mode });
      }
      promise
        .then((text) => {
          candidateTextList.value[i].text = text;
        })
        .finally(() => {
          candidateTextList.value[i].loading = false;
        });
      promises.push(promise);
    }
    await Promise.any(promises);
    const firstValidIdx = candidateTextList.value.findIndex(
      (item) => item.text
    );
    if (firstValidIdx > -1) {
      selectedCandidateIdx.value = firstValidIdx;
      props.editor.commands.addAiTextResults(
        candidateTextList.value[firstValidIdx].text
      );
    }
  } catch (e) {
    emit("update:show", false);
    throw e;
  } finally {
    isAiLoading.value = false;
  }
};
const onAbortCandidate = () => {
  editorUtils.removeAiTextNode(props.editor);
  emit("update:show", false);
  nextTick(() => {
    props.editor.commands.focus();
  });
};

const onCandidateSelect = (idx: number) => {
  selectedCandidateIdx.value = idx;
  props.editor
    .chain()
    .setMeta("addToHistory", false)
    .setNodeSelection(insertPosition.value)
    .updateAttributes("aiTextResults", {
      text: candidateTextList.value[idx].text,
    })
    .run();
};
const onConfirmCandidate = (close = false) => {
  editorUtils.removeAiTextNode(props.editor);
  props.editor!.commands.insertContent(
    candidateTextList.value[selectedCandidateIdx.value].text
  );
  insertPosition.value = editorUtils.getFrom(props.editor);
  if (close) {
    emit("update:show", false);
    nextTick(() => {
      props.editor.commands.focus();
    });
  }
};
const continueWriting = async () => {
  onConfirmCandidate(false);
  await startAiWriting();
};

export interface AiWritingPopupExpose {
  startAiWriting: typeof startAiWriting;
}

defineExpose({
  startAiWriting,
});
</script>
<template>
  <popup
    :close-on-click-overlay="false"
    :lock-scroll="false"
    :overlay="false"
    :overlay-style="{ opacity: 0.5 }"
    :show="show"
    class="max-h-[50%]"
    position="bottom"
    @update:show="emit('update:show', $event)"
  >
    <div
      v-if="isAiLoading"
      class="w-full h-full flex flex-col items-center justify-center gap-4 p-6"
    >
      <span class="loading loading-infinity loading-lg text-primary"></span>
      <div class="text-base">{{ $t("story.g.writingStory") }}</div>
    </div>
    <div v-else class="p-4 flex flex-col gap-2 items-center justify-center">
      <div class="flex w-full justify-between items-center">
        <div class="flex gap-2">
          <button
            class="btn btn-xs md:btn-sm rounded"
            @click="onAbortCandidate"
          >
            <light-arrow-icon class="icon-sm" />
            {{ $t("Buttons.undo") }}
          </button>
          <button
            class="btn btn-xs md:btn-sm rounded"
            @click="onConfirmCandidate(true)"
          >
            <edit-icon class="icon-sm" />
            {{ $t("Buttons.edit") }}
          </button>
        </div>
        <div class="flex gap-2">
          <button
            class="btn btn-xs md:btn-sm rounded"
            @click="startAiWriting()"
          >
            {{ $t("Buttons.switch") }}
          </button>
          <button
            class="btn btn-xs md:btn-sm btn-primary rounded"
            @click="continueWriting"
          >
            <magic-stick-icon class="icon-sm" />
            {{ $t("Buttons.continueWriting") }}
          </button>
        </div>
      </div>
      <div class="text-base font-bold">{{ $t("Message.otherVersion") }}</div>
      <div class="grid grid-cols-3 gap-1 w-full">
        <div
          v-for="(item, i) in candidateTextList"
          :key="i"
          :class="{
            'bg-neutral': item.text,
          }"
          class="border-primary rounded w-full text-xs md:text-sm min-h-[7rem] overflow-hidden border flex items-center justify-center"
          @click="item.text && onCandidateSelect(i)"
        >
          <div
            v-if="item.loading"
            class="loading loading-ring text-primary loading-sm"
          ></div>
          <div
            v-else-if="!item.text"
            class="p-2 text-base-content/50 flex flex-col items-center justify-center gap-2"
          >
            <skull-icon class="icon-xl" />
            {{ $t("Message.nothingWritten") }}
          </div>
          <text-ellipsis
            v-else
            :content="item.text"
            :rows="7"
            class="w-full h-full p-2"
          />
        </div>
      </div>
    </div>
  </popup>
</template>
